import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { usePlayerStore } from '../store/player';
import { content, displayName, useDisplayLocale, type BundledMember, type DisplayLocale } from '../content/bundle';

type SortKey = 'rarity' | 'role' | 'level' | 'acquired';

const RARITY_ORDER: Record<string, number> = { SSR: 0, SR: 1, R: 2, N: 3 };
const RARITY_COLORS: Record<string, string> = {
  SSR: 'border-neon-yellow',
  SR: 'border-neon-pink',
  R: 'border-neon-cyan',
  N: 'border-ink-500'
};
const ROLE_ICONS: Record<string, string> = {
  rapper: '🎤', vocalist: '🎵', dancer: '💃', visual: '✨', maknae: '⭐'
};

interface RosterEntry {
  memberId: string;
  level: number;
  xp: number;
  shards: number;
  equipped?: { passiveId: string; activeIds: string[]; ultimateId: string };
}

function MemberSprite({ memberId, name }: { memberId: string; name: string }) {
  const [errored, setErrored] = useState(false);
  const src = `/stanwars/sprites/members/${memberId}/portrait.png`;
  const group = content.getGroup(content.getMember(memberId)?.groupId ?? '');
  const fallbackColor = group?.colorPrimary ?? '#444';

  if (errored) {
    return (
      <div
        className="w-24 h-24 mx-auto mb-1"
        style={{ backgroundColor: fallbackColor }}
        aria-label={`${name} sprite`}
      />
    );
  }

  return (
    <img
      src={src}
      alt={name}
      role="img"
      aria-label={`${name} sprite`}
      className="w-24 h-24 mx-auto mb-1 block"
      style={{ imageRendering: 'pixelated', objectFit: 'contain' }}
      onError={() => setErrored(true)}
    />
  );
}

function MemberCard({ entry, locale }: { entry: RosterEntry; locale: DisplayLocale }) {
  const { t } = useTranslation();
  const member: BundledMember | undefined = content.getMember(entry.memberId);
  const name = member ? displayName(member, locale) : entry.memberId;

  if (!member) {
    return (
      <Link to={`/member/${entry.memberId}`}>
        <div className="border-2 border-ink-600 p-3 flex flex-col gap-1 opacity-50">
          <PixelText size="sm" color="white">{t('roster.unknown')}</PixelText>
          <PixelText size="sm" color="white">{name}</PixelText>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/member/${member.id}`}>
      <div
        className={`border-2 ${RARITY_COLORS[member.rarity] ?? 'border-ink-500'} p-3 flex flex-col gap-1 hover:opacity-80 transition-opacity cursor-pointer`}
        data-testid="member-card"
      >
        <MemberSprite memberId={member.id} name={name} />
        <PixelText size="sm" color="white">{name}</PixelText>
        <div className="flex justify-between items-center">
          <span className="text-xs">{ROLE_ICONS[member.role]} {member.role}</span>
          <PixelText size="sm" color="yellow">{t('roster.level', { lv: entry.level })}</PixelText>
        </div>
        <PixelText size="sm" color="cyan">{member.rarity}</PixelText>
      </div>
    </Link>
  );
}

export function Roster() {
  const { t } = useTranslation();
  const me = usePlayerStore(s => s.me);
  const [sort, setSort] = useState<SortKey>('rarity');
  const locale = useDisplayLocale();

  if (!me) return <Navigate to="/login" replace />;

  const rosterEntries = me.roster as RosterEntry[];

  const sorted = [...rosterEntries].sort((a, b) => {
    const ma = content.getMember(a.memberId);
    const mb = content.getMember(b.memberId);
    if (sort === 'rarity') {
      return (RARITY_ORDER[ma?.rarity ?? 'N'] ?? 3) - (RARITY_ORDER[mb?.rarity ?? 'N'] ?? 3);
    }
    if (sort === 'role') {
      return (ma?.role ?? '').localeCompare(mb?.role ?? '');
    }
    if (sort === 'level') return b.level - a.level;
    return 0;
  });

  const SORT_TABS: Array<{ key: SortKey; labelKey: string }> = [
    { key: 'rarity',   labelKey: 'roster.sort_rarity' },
    { key: 'role',     labelKey: 'roster.sort_role' },
    { key: 'level',    labelKey: 'roster.sort_level' },
    { key: 'acquired', labelKey: 'roster.sort_acquired' },
  ];

  return (
    <main className="min-h-screen flex flex-col gap-4 p-4 relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/stanwars/sprites/groups/newjeans.png)', backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', imageRendering: 'pixelated', opacity: 0.12 }}
      />
      <div className="flex items-center gap-3">
        <Link to="/"><PixelButton variant="ghost" className="text-xs">{t('common.home')}</PixelButton></Link>
        <PixelText size="xl" color="pink">{t('roster.title')}</PixelText>
      </div>

      {/* Sort tabs */}
      <div className="flex gap-2 flex-wrap">
        {SORT_TABS.map(({ key, labelKey }) => (
          <button
            key={key}
            onClick={() => setSort(key)}
            className={`pixel-text text-xs px-3 py-1 border-2 ${
              sort === key ? 'border-neon-pink text-neon-pink' : 'border-ink-600 text-ink-400'
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {/* Grid */}
      {sorted.length === 0 ? (
        <PixelPanel className="text-center mt-8">
          <PixelText color="yellow">{t('roster.empty')}</PixelText>
          <PixelText size="sm" color="cyan" className="mt-2">
            {t('roster.empty_hint')}
          </PixelText>
          <Link to="/gacha" className="mt-4 block">
            <PixelButton>{t('roster.go_gacha')}</PixelButton>
          </Link>
        </PixelPanel>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {sorted.map(entry => (
            <MemberCard key={entry.memberId} entry={entry} locale={locale} />
          ))}
        </div>
      )}
    </main>
  );
}
